import 'core-js/stable';
import 'regenerator-runtime/runtime';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';

// Load font stylesheet dynamically from env variable.
// VITE_FontStylesheetUrl, when set, is used as-is (supports Adobe Fonts / Typekit etc.).
// Otherwise falls back to Google Fonts built from VITE_FontFamilyDownload.
const fontStylesheetUrl = import.meta.env.VITE_FontStylesheetUrl;
const fontFamily = import.meta.env.VITE_FontFamilyDownload || 'Poppins';
const fontUrl =
    fontStylesheetUrl && fontStylesheetUrl !== '__FONTSTYLESHEETURL__'
        ? fontStylesheetUrl
        : fontFamily && fontFamily !== '__FONTFAMILY__'
          ? `https://fonts.googleapis.com/css?family=${encodeURIComponent(fontFamily)}:300,400,500,600,700&display=swap&subset=latin-ext`
          : null;

if (fontUrl) {
    const preloadLink = document.createElement('link');
    preloadLink.rel = 'preload';
    preloadLink.href = fontUrl;
    preloadLink.as = 'style';
    preloadLink.crossOrigin = 'anonymous';
    document.head.appendChild(preloadLink);

    const styleLink = document.createElement('link');
    styleLink.rel = 'stylesheet';
    styleLink.href = fontUrl;
    document.head.appendChild(styleLink);
}

import {
    ContextProvider,
    contextSaga,
    DocumentProvider,
    ReferenceProvider,
    referenceSaga,
    runCoreSaga,
} from 'metrix-common3';
import { HelmetProvider } from 'react-helmet-async';
import App from './containers/App';
import { enMessages, frMessages } from './messages';
import CRMWebConfigurationProvider from './provider/CRMWebConfiguration';
import AppWithConstants from './provider/constants';
import { unregisterServiceWorker } from './registerServiceWorker';
import configureStore from './store/configureStore';

function getCookie(name) {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match ? match[2] : null;
}

const preferredCulture = getCookie('preferredCulture') || localStorage.getItem('preferredCulture') || 'en-CA';

const originalFetch = window.fetch;
window.fetch = (url, options = {}) => {
    if (!options.headers) {
        options.headers = {};
    }

    options.headers = {
        'Accept-Language': preferredCulture,
        ...options.headers,
    };

    return originalFetch(url, options);
};

const initialState = window.initialReduxState || {};
const store = configureStore(initialState);

// Run core sagas eagerly so the context saga is listening before any
// component mounts and dispatches GET_CONFIGURATION. Otherwise React 18
// StrictMode mounts children before the parent's injectSaga registers,
// the initial dispatch fires into a void, and the app stays on the loader.
//
// Through the injector rather than store.runSaga: the providers inject these same sagas on
// mount, and a bare runSaga leaves no key behind for that injection to find, so it started a
// second watcher and every context action was handled twice.
runCoreSaga(store, 'context', contextSaga);
runCoreSaga(store, 'reference', referenceSaga);

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
    <React.StrictMode>
        <HelmetProvider>
            <Provider store={store}>
                <AppWithConstants>
                    <ContextProvider messages={{ en: enMessages, fr: frMessages }}>
                        <CRMWebConfigurationProvider>
                            <ReferenceProvider>
                                <DocumentProvider>
                                    <App />
                                </DocumentProvider>
                            </ReferenceProvider>
                        </CRMWebConfigurationProvider>
                    </ContextProvider>
                </AppWithConstants>
            </Provider>
        </HelmetProvider>
    </React.StrictMode>,
);

unregisterServiceWorker();

if (import.meta.hot) {
    import.meta.hot.accept();
}
